import type { TextInputProps } from '@mantine/core'
import type { FormErrors, UseFormReturnType } from '@mantine/form'

import { getObjectKeys } from '@/utils/objects'

// Form field names used in validation and scrolling
export type FormFieldName =
  | 'name'
  | 'shortDescription'
  | 'selectEsk'
  | 'presentationMode'
  | 'categories'
  | 'mappings'
  | 'demographic'
  | 'sectionObjectives'
  | 'presentationObjectives'
  | 'introduction'
  | 'personality'
  | 'deliveryStyle'
  | 'guidance'

export type FormSectionName =
  'topicSettings' | 'requiredSettings' | 'deliveryMode' | 'primaryMediaFile' | 'supplementaryContent'

// Field configuration mapping display names, sections, and scroll targets
export const FIELD_CONFIG: Record<
  FormFieldName,
  {
    displayName: string
    section: FormSectionName
    scrollTarget?: FormFieldName // If different from the field itself
  }
> = {
  // Topic Settings Section
  selectEsk: {
    displayName: 'Expert Sidekick',
    section: 'topicSettings',
    scrollTarget: 'categories', // selectEsk is auto-selected via categories dropdown
  },
  categories: { displayName: 'Topic', section: 'topicSettings' },
  mappings: { displayName: 'Sub-Topics', section: 'topicSettings' },

  // Required Settings Section
  name: { displayName: 'Title', section: 'requiredSettings' },
  shortDescription: { displayName: 'Module Description', section: 'requiredSettings' },
  demographic: { displayName: 'Target Audience', section: 'requiredSettings' },
  introduction: { displayName: 'Module Introduction', section: 'requiredSettings' },

  personality: { displayName: 'Personality', section: 'topicSettings' },
  sectionObjectives: { displayName: 'Objectives', section: 'requiredSettings' }, // Moved to required settings when visible
  presentationObjectives: { displayName: 'Objectives', section: 'requiredSettings' },
  deliveryStyle: { displayName: 'Delivery Mode', section: 'deliveryMode' },
  guidance: { displayName: 'Guidance', section: 'requiredSettings' },

  // Primary File Section
  presentationMode: { displayName: 'Presentation Mode', section: 'primaryMediaFile' },
}

/**
 * Returns the display name for a field, falls back to the field name if not found
 */
export function getFieldDisplayName(fieldName: FormFieldName | string): string {
  if (fieldName in FIELD_CONFIG) {
    return FIELD_CONFIG[fieldName as FormFieldName].displayName
  }
  return fieldName
}

/**
 * Returns the form section that contains the given field
 */
export function getFieldSection(fieldName: FormFieldName | string): FormSectionName {
  if (fieldName in FIELD_CONFIG) {
    return FIELD_CONFIG[fieldName as FormFieldName].section
  }
  return 'requiredSettings'
}

/**
 * Returns the field to scroll to, handles redirects for fields without direct inputs
 */
export function getFieldScrollTarget(fieldName: FormFieldName | string): FormFieldName | string {
  if (fieldName in FIELD_CONFIG) {
    const config = FIELD_CONFIG[fieldName as FormFieldName]
    return config.scrollTarget || fieldName
  }
  return fieldName
}

/**
 * Validation result containing validity status and scroll information
 */
export interface ValidationResult {
  isValid: boolean
  firstFailingField?: FormFieldName | string
  failingFields: (FormFieldName | string)[]
  targetSection?: FormSectionName
  scrollTarget?: FormFieldName | string
}

/**
 * Validates form fields and returns scroll information for the first failing field
 */
export function validateFieldsList<T extends UseFormReturnType<any>>(
  form: T,
  fields: (keyof T['values'])[],
  errors: FormErrors = form.errors,
): ValidationResult {
  const errorKeys = getObjectKeys(errors)
  // Use getValues() instead of form.values so this works correctly in
  // uncontrolled mode where form.values may be stale.
  const currentValues = form.getValues()

  const failingFields = fields.filter(f => {
    const v = currentValues[f]
    return v === undefined || v === null || v === '' || errorKeys.includes(f as string)
  }) as (FormFieldName | string)[]

  if (failingFields.length === 0) {
    return { isValid: true, failingFields: [] }
  }

  const firstFailingField = failingFields[0]
  const targetSection = getFieldSection(firstFailingField)
  const scrollTarget = getFieldScrollTarget(firstFailingField)

  return {
    isValid: false,
    firstFailingField,
    failingFields,
    targetSection,
    scrollTarget,
  }
}

export function getRequiredFieldDescriptionProps<T extends UseFormReturnType<any>>(
  form: T,
  field: keyof T['values'],
  description: string,
): Pick<TextInputProps, 'description' | 'error'> {
  const error = form.errors[field as string]

  // The description doubles as the error copy so the text under a field doesn't move on
  // failure. A field without one falls back to the validation message: an empty string
  // reads as "no error" to Mantine, leaving the field unmarked (SME-1127).
  return {
    description,
    error: error ? description || error : undefined,
  }
}
